import * as React from 'react';
import type {NativeId} from '@packages/systems/core/utils/NativeIdUtils';
import {createAction, ActionType} from '@packages/utilities/create-action';

/**
 * Metadata for slider navigation actions dispatched from the mini toolbar.
 * Consumed by SliderWrapperType to replay the correct slide navigation
 * after focusing into a component instance.
 */
export type SliderNavigationMetadata =
  | {type: 'prev'}
  | {type: 'next'}
  | {type: 'immediate'; slideId: string};

/**
 * Discriminated union of all mini toolbar metadata types.
 * Extend this union as other mini toolbar buttons add metadata.
 */
export type MiniToolbarMetadata = SliderNavigationMetadata;

/**
 * Compact MiniToolbar controls can call this to request focus-then-action.
 * CompactDataTypeRenderer dispatches the pending action.
 */
export const MiniToolbarInstanceProxyContext = React.createContext<{
  dispatchPendingAction: (metadata?: MiniToolbarMetadata) => void;
} | null>(null);

export const useMiniToolbarInstanceProxy = () => {
  if (!MiniToolbarInstanceProxyContext) {
    throw new Error(
      'useMiniToolbarInstanceProxy must be used within a MiniToolbarInstanceProxyProvider'
    );
  }
  return React.useContext(MiniToolbarInstanceProxyContext);
};

export const MiniToolbarButtons = {
  EDIT_ON_CANVAS: 'edit-on-canvas',
  LOCK_EDIT_ON_CANVAS: 'lock-edit-on-canvas',
  LINK_MINI_SETTINGS: 'link-mini-settings',
  EDIT_IMAGE_ASSET_PANEL: 'edit-image-asset-panel',
  COLOR_THEME: 'color-theme',
  TABS_NAVIGATION: 'tabs-navigation',
  SLIDER_NAVIGATION: 'slider-navigation',
  DROPDOWN_TOGGLE: 'dropdown-toggle',
  NAVBAR_TOGGLE: 'navbar-toggle',
  POPOVER_TOGGLE: 'popover-toggle',
  IMAGE_MINI_SETTINGS: 'image-mini-settings',
  TRANSLATE_TO_LOCALE: 'translate-to-locale',
  RESET_LOCALE: 'reset-locale',
  MINI_SETTINGS: 'mini-settings',
  ADD_COMPONENT: 'add-component',
  DELETE_COMPONENT: 'delete-component',
  OPEN_CMS_COLLECTION: 'open-cms-collection',
  GENERATE_AI_TEXT: 'generate-ai-text',
  OPTIMIZATION_RESET_TO_BASE: 'optimization-reset-to-base',
  MOVE_SECTION_UP: 'move-section-up',
  MOVE_SECTION_DOWN: 'move-section-down',
  DUPLICATE_COMPONENT: 'duplicate-component',
} as const;

export type MiniToolbarButtonsType =
  (typeof MiniToolbarButtons)[keyof typeof MiniToolbarButtons];

export const PENDING_EDIT_TYPE = {
  MINI_TOOLBAR: 'miniToolbar',
  DOUBLE_CLICK: 'doubleClick',
} as const;

/**
 * Pending MiniToolbar action for Edit Site Mode.
 * Used for the "focus-then-execute" pattern when editing unbound elements
 * in component instances.
 */
export type PendingMiniToolbarAction = {
  type: typeof PENDING_EDIT_TYPE.MINI_TOOLBAR;
  buttonType: MiniToolbarButtonsType;
  nativeIdPath: NativeId[];
  metadata?: MiniToolbarMetadata;
};

/**
 * Pending double-click action for Edit Site Mode.
 * Used for the "focus-then-execute" pattern when double-clicking unbound elements
 * in component instances.
 *
 * On replay, the eventTarget is re-derived from the DOM using
 * rawDoubleClickedNativeIdPath via getNativeNodeByNativeIdPath,
 * avoiding stale DOM references in state.
 */
export type PendingDoubleClickAction = {
  type: typeof PENDING_EDIT_TYPE.DOUBLE_CLICK;
  nativeIdPath: NativeId[];
  rawDoubleClickedNativeIdPath: NativeId[];
  clientX: number;
  clientY: number;
};

export type PendingEditAction =
  | PendingMiniToolbarAction
  | PendingDoubleClickAction;

export const PENDING_EDIT_ACTION_CLEARED =
  'PENDING_EDIT_ACTION_CLEARED' as const;

/**
 * Clears the pending edit action after it has been executed.
 */
export const clearPendingEditAction = createAction<
  typeof PENDING_EDIT_ACTION_CLEARED,
  void
>(PENDING_EDIT_ACTION_CLEARED);

/**
 * Selects the pending MiniToolbar action if one exists.
 * Returns null if the pending action is a double-click action.
 */
export const selectPendingMiniToolbarAction = (state: {
  UiNodeStore: {pendingEditAction: PendingEditAction | null};
}): PendingMiniToolbarAction | null => {
  const pendingAction = state.UiNodeStore.pendingEditAction;
  if (pendingAction?.type === PENDING_EDIT_TYPE.MINI_TOOLBAR) {
    return pendingAction;
  }
  return null;
};

export const PENDING_MINI_TOOLBAR_ACTION_SET =
  'PENDING_MINI_TOOLBAR_ACTION_SET' as const;
export const PENDING_DOUBLE_CLICK_ACTION_SET =
  'PENDING_DOUBLE_CLICK_ACTION_SET' as const;

type PendingMiniToolbarActionPayload = {
  buttonType: MiniToolbarButtonsType;
  nativeIdPath: NativeId[];
  metadata?: MiniToolbarMetadata;
  /**
   * Pass `false` when the caller has already put the selection where the action
   * should run. Focusing would move the edit into the component definition, which
   * is wrong for content the caller only just promoted onto the instance.
   */
  shouldFocus?: boolean;
};

type PendingDoubleClickActionPayload = {
  nativeIdPath: NativeId[];
  rawDoubleClickedNativeIdPath: NativeId[];
  clientX: number;
  clientY: number;
};

/**
 * Sets a pending MiniToolbar action to be executed after focusing into a component.
 * Used for the "focus-then-execute" pattern for unbound elements.
 */
export const setPendingMiniToolbarAction = createAction<
  typeof PENDING_MINI_TOOLBAR_ACTION_SET,
  PendingMiniToolbarActionPayload
>(PENDING_MINI_TOOLBAR_ACTION_SET);

/**
 * Sets a pending double-click action to be executed after focusing into a component.
 * Used for the "focus-then-execute" pattern when double-clicking unbound text elements.
 * Stores coordinates and the nativeIdPath so the replay path can re-derive the element from the DOM.
 */
export const setPendingDoubleClickAction = createAction<
  typeof PENDING_DOUBLE_CLICK_ACTION_SET,
  PendingDoubleClickActionPayload
>(PENDING_DOUBLE_CLICK_ACTION_SET);

export type FocusThenEditAction =
  | ActionType<typeof setPendingMiniToolbarAction>
  | ActionType<typeof setPendingDoubleClickAction>
  | ActionType<typeof clearPendingEditAction>;
